import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutArticles } from '@/mocks/articles';

const contactInfo = {
  email: 'support@rwgjqtpido.cn',
  weibo: '@紅桃視頻官方',
  wechat: 'hongtao_video',
  address: '北京市朝阳区望京科技园A座',
  phone: '400-888-6666',
};

const milestones = [
  { year: '2022', title: '梦想起航', desc: '创始团队成立，紅桃視頻App 1.0版本正式上线' },
  { year: '2023', title: '快速成长', desc: '注册用户突破500万，完成A轮融资，团队扩展至100人' },
  { year: '2024', title: '行业突破', desc: '月活用户突破1000万，获中国科技创新峰会技术创新奖' },
  { year: '2025', title: '生态完善', desc: '推出创作者扶持计划，日更视频突破50万条' },
  { year: '2026', title: '引领潮流', desc: '注册用户突破5000万，成为行业领先的短视频平台' },
];

const teamMembers = [
  { name: '张明远', role: '创始人 & CEO', desc: '前互联网大厂产品总监，15年产品经验' },
  { name: '李思涵', role: '联合创始人 & CTO', desc: 'AI算法专家，前微软研究院研究员' },
  { name: '王晓峰', role: '内容运营副总裁', desc: '10年内容行业经验，打造多个千万级IP' },
  { name: '陈雨萱', role: '产品设计总监', desc: '红点设计奖得主，专注用户体验设计' },
];

const awards = [
  { title: '年度最佳短视频平台', org: '中国互联网协会', year: '2026' },
  { title: '用户满意度第一', org: '艾媒咨询', year: '2025' },
  { title: '年度最受欢迎应用', org: '华为应用市场', year: '2025' },
  { title: '金米奖最佳视频应用', org: '小米应用商店', year: '2024' },
  { title: '技术创新奖', org: '中国科技创新峰会', year: '2024' },
];

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'brand' | 'reputation' | 'contact'>('brand');

  const tabs = [
    { key: 'brand' as const, label: '品牌介绍', icon: 'ri-building-2-line' },
    { key: 'reputation' as const, label: '口碑声誉', icon: 'ri-star-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-customer-service-2-line' },
  ];

  const brandArticle = aboutArticles[0];
  const reputationArticle = aboutArticles[1];

  return (
    <>
      <SEOMeta
        title="关于我们 - 紅桃視頻"
        description="紅桃視頻品牌介绍，了解紅桃視頻的发展历程、团队背景、行业荣誉和联系方式。高清短视频平台，5000万用户的选择。"
        keywords="紅桃視頻,关于紅桃視頻,紅桃視頻品牌,紅桃視頻联系方式,紅桃視頻团队"
        canonicalUrl="https://rwgjqtpido.cn/about"
      />
      <SchemaOrg
        type="Organization"
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '紅桃視頻',
          url: 'https://rwgjqtpido.cn',
          description: '高清短視頻平台，海量內容免費看',
          email: contactInfo.email,
          telephone: contactInfo.phone,
          address: {
            '@type': 'PostalAddress',
            addressLocality: '北京',
            addressRegion: '朝阳区',
            streetAddress: '望京科技园A座',
          },
        }}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  关于紅桃視頻
                </h1>
                <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                  了解我们的故事、理念和团队，以及如何与我们取得联系
                </p>
              </div>

              <div className="flex items-center justify-center mb-10">
                <div className="inline-flex bg-background-200/70 rounded-full px-1 py-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`whitespace-nowrap flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                        activeTab === tab.key
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      <i className={`${tab.icon} text-sm`}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {activeTab === 'brand' && (
                <div>
                  <div className="rounded-lg overflow-hidden mb-8">
                    <img
                      src={brandArticle.image}
                      alt="紅桃視頻品牌故事"
                      title="紅桃視頻品牌故事"
                      className="w-full aspect-[2.5/1] object-cover"
                    />
                  </div>
                  <div className="max-w-3xl mx-auto">
                    <div
                      className="prose prose-lg max-w-none
                        prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                        prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                        prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                        prose-strong:text-foreground-900 prose-strong:font-semibold
                        prose-ul:text-foreground-700 prose-li:mb-2
                        [&_ul]:list-disc [&_ul]:pl-5"
                      dangerouslySetInnerHTML={{ __html: brandArticle.content }}
                    />

                    <div className="mt-12">
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
                        发展历程
                      </h2>
                      <div className="relative">
                        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 md:-translate-x-px"></div>
                        <div className="space-y-8">
                          {milestones.map((m, idx) => (
                            <div key={m.year} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                              <div className="flex-1 md:text-right hidden md:block">
                                {idx % 2 === 0 && (
                                  <div className="pr-6">
                                    <h3 className="font-heading font-bold text-foreground-900">{m.title}</h3>
                                    <p className="text-sm text-foreground-500 mt-1">{m.desc}</p>
                                  </div>
                                )}
                              </div>
                              <div className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0">
                                <span className="text-xs font-bold">{m.year.slice(2)}</span>
                              </div>
                              <div className={`flex-1 ${idx % 2 !== 0 ? 'md:text-left' : ''}`}>
                                <div className={idx % 2 !== 0 ? 'md:pl-6' : 'pl-6 md:hidden'}>
                                  <span className="text-xs font-bold text-primary-500">{m.year}</span>
                                  <h3 className="font-heading font-bold text-foreground-900">{m.title}</h3>
                                  <p className="text-sm text-foreground-500 mt-1">{m.desc}</p>
                                </div>
                              </div>
                              <div className="hidden md:block flex-1">
                                {idx % 2 !== 0 && (
                                  <div className="pl-6">
                                    <span className="text-xs font-bold text-primary-500">{m.year}</span>
                                    <h3 className="font-heading font-bold text-foreground-900">{m.title}</h3>
                                    <p className="text-sm text-foreground-500 mt-1">{m.desc}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-14">
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
                        核心团队
                      </h2>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {teamMembers.map((member) => (
                          <div key={member.name} className="text-center p-5 rounded-lg bg-background-50">
                            <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center bg-secondary-100 text-secondary-700">
                              <i className="ri-user-3-line text-2xl"></i>
                            </div>
                            <h3 className="text-sm font-medium text-foreground-900">{member.name}</h3>
                            <p className="text-xs text-primary-500 mt-1">{member.role}</p>
                            <p className="text-xs text-foreground-500 mt-2 leading-relaxed">{member.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'reputation' && (
                <div className="max-w-3xl mx-auto">
                  <div className="rounded-lg overflow-hidden mb-8">
                    <img
                      src={reputationArticle.image}
                      alt="紅桃視頻荣誉与认可"
                      title="紅桃視頻荣誉与认可"
                      className="w-full aspect-[2.5/1] object-cover"
                    />
                  </div>
                  <div
                    className="prose prose-lg max-w-none
                      prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                      prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                      prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                      prose-strong:text-foreground-900 prose-strong:font-semibold
                      prose-ul:text-foreground-700 prose-li:mb-2
                      [&_ul]:list-disc [&_ul]:pl-5"
                    dangerouslySetInnerHTML={{ __html: reputationArticle.content }}
                  />

                  <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {awards.map((award) => (
                      <div key={award.title} className="p-5 rounded-lg bg-background-50 text-center">
                        <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                          <i className="ri-award-line text-xl"></i>
                        </div>
                        <h3 className="text-sm font-medium text-foreground-900 mb-1">{award.title}</h3>
                        <p className="text-xs text-foreground-500">{award.org}</p>
                        <p className="text-xs text-accent-500 mt-1">{award.year}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'contact' && (
                <div className="max-w-4xl mx-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                        联系方式
                      </h2>
                      <div className="space-y-5">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                            <i className="ri-mail-line text-lg"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                            <a href={`mailto:${contactInfo.email}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200">
                              {contactInfo.email}
                            </a>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                            <i className="ri-phone-line text-lg"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-900">客服电话</p>
                            <p className="text-sm text-primary-500">{contactInfo.phone}</p>
                            <p className="text-xs text-foreground-500 mt-1">工作时间：周一至周日 9:00 - 21:00</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                            <i className="ri-map-pin-line text-lg"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-900">公司地址</p>
                            <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                            <i className="ri-weibo-line text-lg"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-900">官方微博</p>
                            <p className="text-sm text-foreground-600">{contactInfo.weibo}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                            <i className="ri-wechat-line text-lg"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                            <p className="text-sm text-foreground-600">{contactInfo.wechat}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                        给我们留言
                      </h2>
                      <ContactForm />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setSubmitted(true);
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qsr66e3oq4jqumtq8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setError('网络错误，请检查连接后重试');
    }
  };

  if (submitted) {
    return (
      <div className="p-8 rounded-lg bg-background-50 text-center">
        <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-check-line text-2xl"></i>
        </div>
        <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">留言已发送</h3>
        <p className="text-sm text-foreground-500 mb-4">感谢您的留言，我们会在24小时内回复您。</p>
        <button
          onClick={() => setSubmitted(false)}
          className="whitespace-nowrap px-5 py-2 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200"
        >
          继续留言
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-6 rounded-lg bg-background-50" data-readdy-form="">
      <div>
        <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
          姓名 <span className="text-primary-500">*</span>
        </label>
        <input
          type="text"
          id="contact_name"
          name="name"
          required
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
          邮箱 <span className="text-primary-500">*</span>
        </label>
        <input
          type="email"
          id="contact_email"
          name="email"
          required
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
          placeholder="请输入您的电子邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题
        </label>
        <input
          type="text"
          id="contact_subject"
          name="subject"
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
          placeholder="请输入留言主题"
        />
      </div>
      <div>
        <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact_message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
          placeholder="请输入您的留言内容（最多500字）"
        ></textarea>
      </div>
      <input
        type="text"
        name="phone_alt"
        className="anti-spam-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
      <button
        type="submit"
        className="whitespace-nowrap w-full px-6 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200"
      >
        发送留言
      </button>
    </form>
  );
}